import { Component, ElementRef, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { OfferFieldConfig, OFFER_FIELDS, getOfferPricingFields } from '../../configs/offer-pricing-field.config';
import { OrganizationService, Organization } from 'src/app/@shared';
import { isPropertyModifiedInList, getPreviousStepOldValue as lookupPreviousStepOldValue } from '../../utilities/previous-step-changes.util';

@Component({
  selector: 'app-offer-pricing-form',
  templateUrl: './offer-pricing-form.component.html',
  styleUrls: ['./offer-pricing-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfferPricingFormComponent implements OnInit, OnChanges, OnDestroy {
  offerTypeName!: string | null;
  @Input() offerTypeId!: string | null;
  @Input() offertypes!: any;
  @Input() offerDetails: any = null;
  @Input() canEdit = true;
  @Input() emitAllPricingFields = true;
  @Input() modifiedPropertyNames: string[] = [];
  @Input() modifiedPropertyOldValues: Record<string, string> = {};
  @Input() showChangesHighlight = false;

  @Output() formValueChange = new EventEmitter<Record<string, any>>();
  @Output() formValidityChange = new EventEmitter<boolean>();

  form!: FormGroup;
  pricingFields: OfferFieldConfig[] = [];
  private organization!: Organization;

  private destroy$ = new Subject<void>();
  private applyingExternalPatch = false;

  constructor(
    private fb: FormBuilder,
    private organizationService: OrganizationService,
    private elementRef: ElementRef<HTMLElement>
  ) { }

  ngOnInit(): void {
    this.organizationService.organization$
      .pipe(filter(Boolean), takeUntil(this.destroy$))
      .subscribe(org => {
        this.organization = org!;
        this.rebuildForm();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['offerTypeId'] && !changes['offerTypeId'].firstChange || changes['offertypes']) {
      this.rebuildForm();
    }

    if (changes['offerDetails'] && this.form) {
      this.patchInitialValues();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private rebuildForm(): void {
    if (!this.organization) return;

    if (this.offerTypeId === null || this.offerTypeId === 'null' || this.offerTypeId === 'none' || this.offerTypeId === undefined) {
      this.offerTypeName = 'None';
    } else {
      this.offerTypeName = this.getOfferTypeNameById(this.offerTypeId);
    }

    // console.table([{
    //   Organization: this.organization?.name,
    //   OfferTypeId: this.offerTypeId,
    //   OfferTypeName: this.offerTypeName
    // }]);

    this.pricingFields = getOfferPricingFields(this.offerTypeName || '', this.organization.name);

    const group: Record<string, any> = {};
    this.pricingFields.forEach(field => {
      group[field.key] = [field.defaultValue === -1 ? null : field.defaultValue, field.validators || []];
    });

    this.form = this.fb.group(group, { updateOn: 'blur' });

    this.patchInitialValues();
    this.listenToChanges();
    this.formValidityChange.emit(this.form.valid);
  }

  private patchInitialValues(): void {
    if (!this.offerDetails) return;

    const patch: Record<string, any> = {};

    this.pricingFields.forEach(field => {
      if (this.offerDetails.hasOwnProperty(field.key)) {
        const val = this.offerDetails[field.key];
        patch[field.key] = val === -1 ? null : val;
      }
    });

    this.form.patchValue(patch, { emitEvent: false });
    this.emitFormValue(this.form.getRawValue());
  }

  private listenToChanges(): void {
    this.form.valueChanges
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(value => {
        if (this.applyingExternalPatch) return;
        this.emitFormValue(value);
        this.formValidityChange.emit(this.form.valid);
      });
  }

  applyExternalValuePatch(patch: Record<string, any>) {
    if (!this.form) return;
    this.applyingExternalPatch = true;
    try {
      this.form.patchValue(patch, { emitEvent: false });
      this.emitFormValue(this.form.getRawValue());
      this.formValidityChange.emit(this.form.valid);
    } finally {
      this.applyingExternalPatch = false;
    }
  }

  commitPendingEdits(): void {
    if (!this.form) return;

    const active = document.activeElement as HTMLElement | null;
    if (active && this.elementRef.nativeElement.contains(active)) {
      active.blur();
    }

    this.emitFormValue(this.form.getRawValue());
    this.formValidityChange.emit(this.form.valid);
  }

  isPropertyModified(propertyKey: string): boolean {
    return isPropertyModifiedInList(
      this.modifiedPropertyNames,
      this.showChangesHighlight,
      propertyKey
    );
  }

  getPreviousStepOldValue(propertyKey: string): string | null {
    return lookupPreviousStepOldValue(
      this.modifiedPropertyOldValues,
      this.showChangesHighlight,
      propertyKey
    );
  }

  private emitFormValue(rawValue: any): void {
    const normalized = this.normalizeForEmit(rawValue);

    if (!this.emitAllPricingFields) {
      const payload = this.applyTypeSafety(normalized);
      this.formValueChange.emit(payload);
      return;
    }

    const payload: Record<string, any> = {};
    OFFER_FIELDS.forEach(field => {
      const value = normalized[field.key];
      payload[field.key] = this.coerceByType(value, field);
    });

    this.formValueChange.emit(payload);
  }

  private normalizeForEmit(value: any): any {
    if (value === null) return -1;

    if (Array.isArray(value)) {
      return value.map(v => this.normalizeForEmit(v));
    }

    if (typeof value === 'object') {
      return Object.keys(value).reduce((acc, k) => {
        acc[k] = this.normalizeForEmit(value[k]);
        return acc;
      }, {} as any);
    }

    if (typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))) {
      return Number(value);
    }

    return value;
  }

  private applyTypeSafety(value: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};

    Object.keys(value).forEach(key => {
      const field = OFFER_FIELDS.find(f => f.key === key);
      result[key] = field ? this.coerceByType(value[key], field) : value[key];
    });

    return result;
  }

  private coerceByType(value: any, field: OfferFieldConfig): any {
    const def = field.defaultValue ?? -1;

    if (value === null || value === undefined) {
      return def;
    }

    switch (field.type) {
      case 'number':
        return typeof value === 'number' && !isNaN(value)
          ? value
          : def;

      case 'text':
      case 'textarea':
        return typeof value === 'string' || typeof value === 'number'
          ? value.toString()
          : def;

      default:
        return value;
    }
  }

  private getOfferTypeNameById(offerTypeId: string): string {
    const offerType = Array.isArray(this.offertypes) ? this.offertypes.find((ot: any) => ot.Id === offerTypeId) : '';
    return offerType ? offerType.OfferTypeName : '';
  }

  getFxFlex(index: number): string {
    return index % 3 === 2 ? 'auto' : '33';
  }
}
